import { DeepReadonlyObject, MangoQueryNoLimit, RxCollection } from 'rxdb';
import {
	BehaviorSubject,
	catchError,
	defer,
	EMPTY,
	from,
	map,
	NEVER,
	Observable,
	Subject,
	Subscription,
	take,
	withLatestFrom,
} from 'rxjs';
import {
	distinctUntilChanged,
	exhaustMap,
	filter,
	first,
	switchMap,
	tap,
} from 'rxjs/operators';
import invariant from 'tiny-invariant';

import {
	type AlternativesDirectionEnum,
	PrivateTripSearchRequestDto,
	SearchStatusEnum,
	TCapacityCheckProcessInstance,
} from '@mopla/data-models';
import { Logger } from '@mopla/logger';
import { getCountdownTimer$ } from '@mopla/utils';

import type { BusinessLayer } from '../../business-logic';
import type {
	ICapacityCheckDocType,
	TCapacityCheckRxDocument,
} from '../../schemas/capacityCheck';

import { LBODTFLEXApi } from './api/lbodtflexAPI';
import { PRODTApi } from './api/prodtAPI';
import {
	IGenericCapacityCheckAPI,
	TPostCapacityCheckPayload,
} from './api/types';
import {
	CapacityCheckPoller,
	SUCCESS_POLLING_INTERVAL_MS,
} from './CapacityCheckPoller';

/**
 * TTL keep-alive constants (MC-10032).
 * `POLLING_TTL_SECONDS` is sent with every poll PATCH so the BE keeps the reservation
 * alive while the user is actively in the flow. It is intentionally larger than 2x
 * the slowest polling interval so a single missed poll does not drop the reservation.
 *
 * `BANK_REDIRECT_TTL_SECONDS` is used right before the user is redirected to a 3rd-party
 * payment page (Stripe, TrueLayer). The longer window covers the time the user
 * spends outside of the app.
 */
export const POLLING_TTL_SECONDS = 20;
export const BANK_REDIRECT_TTL_SECONDS = 300;

export type TCapacityCheckOptions = {
	/**
	 * When true, switches polling/resume to the v2 PATCH endpoint and sends
	 * `timeToLive` on POST/PATCH. Default false → legacy GET-poll behavior (V1).
	 */
	useTTL?: boolean;
};

export abstract class CapacityCheck<
	ItineraryType extends 'LBODTFLEX' | 'PRODT'
> {
	public id = Math.random().toString(36).slice(2, 12);
	public isInitialized = false;
	private capacityCheckRxDoc$ =
		new BehaviorSubject<TCapacityCheckRxDocument<ItineraryType> | null>(null);
	public capacityCheckError$ = new Subject<Error>();
	private __teardownRequest$ = new Subject<{ silent: boolean }>();
	private __teardownSub: Subscription;
	private __isRunning = new BehaviorSubject(false);
	/** True while a poll is armed; false on teardown or when the loop self-stops on a terminal status. */
	private __isPolling = new BehaviorSubject(false);

	/**
	 * Owns the polling loop. The `poll` callback captures {@link writeGeneration}
	 * before the request and writes via {@link applyServerState}, preserving the
	 * stale-response guard. Callbacks read `this.*` lazily, so building it here
	 * (before the subclass assigns `capacityCheckAPI`) is safe.
	 */
	private poller = new CapacityCheckPoller<
		TCapacityCheckProcessInstance<ItineraryType>
	>({
		poll: async (capacityCheckId) => {
			const { useTTL } = this.options;
			const generation = this.writeGeneration;

			const res: TCapacityCheckProcessInstance<ItineraryType> = useTTL
				? await this.capacityCheckAPI.patchSearchV2(capacityCheckId, {
						timeToLive:
							// eslint-disable-next-line @typescript-eslint/no-explicit-any
							(window as any).__POLLING_TTL_SECONDS ?? this.currentTTLSeconds,
				  })
				: await this.capacityCheckAPI.getSearch(capacityCheckId);

			await this.applyServerState(generation, res);

			return res;
		},
		getNextInterval: (res) =>
			res.status === SearchStatusEnum.SUCCESS
				? SUCCESS_POLLING_INTERVAL_MS
				: null,
		shouldStop: (res) =>
			res.status === SearchStatusEnum.CANCELLED ||
			res.status === SearchStatusEnum.EXPIRED ||
			res.status === SearchStatusEnum.NOTHING_FOUND,
		onError: (err) => {
			this.logger.error('polling error', err);
			this.capacityCheckError$.next(err as Error);
			this.destroy(true);
		},
	});

	/**
	 * Write generation ("search epoch"). Incremented on every FE-initiated
	 * discontinuity of the search state machine: resume() (round change),
	 * teardown (search death), init()/restore() (new doc identity).
	 *
	 * Server responses may only be written to the doc if the generation has not
	 * changed since their request was issued (see {@link applyServerState}).
	 * This guards against in-flight responses landing after the state they
	 * describe was superseded — RxJS unsubscription cannot cancel an already
	 * running request, so its late response would otherwise overwrite the doc
	 * with a stale snapshot (phantom AlternativeVehicleTypeSheet bug).
	 *
	 * Read-only operations (polling, extendTTL) never bump the generation:
	 * snapshots within one generation are interchangeable, last write wins.
	 */
	private writeGeneration = 0;

	/**
	 * TTL sent with every POST/PATCH when `useTTL` is on. Mutated synchronously
	 * by {@link extendTTL} so that any polling PATCH that fires right after
	 * picks up the extended value instead of racing it back down to POLLING_TTL_SECONDS.
	 */
	private currentTTLSeconds = POLLING_TTL_SECONDS;

	protected capacityCheckAPI!: IGenericCapacityCheckAPI<ItineraryType>;
	protected mode!: 'LBODTFLEX' | 'PRODT';

	private logger = Logger.getInstance('CapacityCheck');

	protected constructor(
		protected bl: BusinessLayer,
		protected options: TCapacityCheckOptions = {}
	) {
		this.__teardownSub = this.initTeardownRequests();

		this.logger.setContext({
			capacityCheckId: this.id,
		});
	}

	private get capacityCheckRxDoc() {
		const capacityCheckRxDoc = this.capacityCheckRxDoc$?.getValue();

		return capacityCheckRxDoc?.getLatest();
	}

	protected get ensureCapacityCheckRxDoc() {
		const capacityCheckRxDoc = this.capacityCheckRxDoc;

		if (!capacityCheckRxDoc) {
			throw new Error('CapacityCheckRxDoc is not initialized');
		}

		return capacityCheckRxDoc;
	}

	private async updateCapacityCheckDoc(
		update: TCapacityCheckProcessInstance<ItineraryType>
	) {
		const doc = this.ensureCapacityCheckRxDoc;
		const { id: capacityCheckId, ...updateDataWithoutId } = update;

		await doc.incrementalModify((prevDoc) => ({
			...prevDoc,
			...updateDataWithoutId,
			capacityCheckId,
		}));
	}

	/**
	 * Writes a server response to the doc only if it belongs to the current
	 * {@link writeGeneration}. `generation` must be captured right before the
	 * request is issued. Stale responses are dropped (with a warn log): the
	 * search state they describe was superseded by resume/teardown while the
	 * request was in flight.
	 */
	private async applyServerState(
		generation: number,
		res: TCapacityCheckProcessInstance<ItineraryType>
	) {
		if (generation !== this.writeGeneration) {
			this.logger.warn('stale response dropped', {
				generationAtRequest: generation,
				writeGeneration: this.writeGeneration,
				staleStatus: res.status,
				staleRoundIndex: res.progress?.roundIndex,
				staleAlternativeVehicleType: res.result?.alternativeVehicleType,
			});
			return;
		}

		await this.updateCapacityCheckDoc(res);
	}

	public async init(existingDoc?: TCapacityCheckRxDocument<ItineraryType>) {
		try {
			/** New doc identity — responses issued against a previous doc are stale */
			this.writeGeneration++;

			const nextCapacityCheckRxDoc =
				existingDoc ||
				(await this.capacityCheckRxCollection.upsert({
					id: this.id,
					status: SearchStatusEnum.IN_PROGRESS,
					mode: this.mode,
				}));

			this.capacityCheckRxDoc$.next(nextCapacityCheckRxDoc);

			this.isInitialized = true;
		} catch (e: any) {
			this.logger.error('init error', e);
			this.capacityCheckError$.next(e);
			this.destroy(true);
			/** Rethrow so the caller can stop before start(). */
			throw e;
		}
	}

	public async restore(capacityCheckId: string) {
		try {
			/** New doc identity — responses issued against a previous doc are stale */
			this.writeGeneration++;

			const queryDescriptor = {
				selector: { capacityCheckId: { $eq: capacityCheckId } },
			} as MangoQueryNoLimit<ICapacityCheckDocType<ItineraryType>>;

			const existingDoc = await this.capacityCheckRxCollection
				.findOne(queryDescriptor)
				.exec();

			invariant(
				existingDoc,
				'[CapacityCheck] Could not restore a capacity check: existingDoc is not found'
			);

			this.capacityCheckRxDoc$.next(existingDoc);

			if (
				[SearchStatusEnum.IN_PROGRESS, SearchStatusEnum.SUCCESS].includes(
					existingDoc.status
				)
			) {
				this.startPolling();
			}
		} catch (e: any) {
			this.logger.error('restore error', e);
			this.capacityCheckError$.next(e);
			this.destroy(true);
			/** Rethrow so the caller can surface the error to the user. */
			throw e;
		}
	}

	/** Marks suggested alternative vehicleClass as acknowledged. Cleared on {@link resume}. */
	public async acknowledgeAlternative(): Promise<void> {
		const doc = this.ensureCapacityCheckRxDoc;
		await doc.incrementalModify((prev) => ({
			...prev,
			alternativeAcknowledged: true,
		}));
	}

	public async start(
		payload: TPostCapacityCheckPayload<ItineraryType>
	): Promise<void> {
		this.__isRunning.next(true);

		try {
			/** Initiating the search with the POST request */
			const finalPayload: TPostCapacityCheckPayload<ItineraryType> = this
				.options.useTTL
				? { ...payload, timeToLive: this.currentTTLSeconds }
				: payload;

			const capacityCheckStateResponse = await this.capacityCheckAPI.postSearch(
				finalPayload
			);

			/**
			 * Deliberately NOT gated by writeGeneration: the teardown handshake
			 * (see initTeardownRequests) waits for `capacityCheckId` from this very
			 * write when destroy() is called while the POST is in flight. Dropping
			 * it would deadlock the teardown. No generation can change mid-start:
			 * resume/teardown both require this write to have landed first.
			 */
			await this.updateCapacityCheckDoc(capacityCheckStateResponse);

			if (
				[SearchStatusEnum.IN_PROGRESS, SearchStatusEnum.SUCCESS].includes(
					capacityCheckStateResponse.status
				)
			) {
				this.startPolling();
			}
		} catch (e: any) {
			this.logger.error('start error', e);
			this.capacityCheckError$.next(e);
			this.destroy(true);
		}
	}

	public async resume(alternativesDirection: AlternativesDirectionEnum) {
		/** Resuming the search by its id with the PATCH request and setting further search direction */
		try {
			/**
			 * Round transition — a new generation. Any response still in flight
			 * (polling PATCH, extendTTL) describes the previous round and must not
			 * overwrite the optimistic update below.
			 */
			this.writeGeneration++;
			const generation = this.writeGeneration;

			this.poller.stop();
			this.poller.resetInterval();

			const doc = this.ensureCapacityCheckRxDoc;

			invariant(
				doc.capacityCheckId,
				'[CapacityCheck] Trying to resume a search without capacityCheckId'
			);

			const currentRoundIndex = doc.progress?.roundIndex ?? 0;
			const nextRoundIndex = currentRoundIndex + 1;

			/**
			 * Optimistic update.
			 * We always assume that the next round after resuming a capacityCheck gets increased by 1.
			 * We also set the next status and update `requestedParams` optimistically
			 * */
			await doc.incrementalModify((oldDoc) => {
				oldDoc.progress = {
					...oldDoc.progress,
					roundIndex: (oldDoc.progress?.roundIndex || 0) + 1,
				} as TCapacityCheckProcessInstance<ItineraryType>['progress'];

				oldDoc.status = SearchStatusEnum.IN_PROGRESS;
				oldDoc.requestedParams = {
					...oldDoc.requestedParams,
					alternativesDirection,
				};

				/** A new round means any previously-accepted alternative class no longer
				 *  applies — clear so the next SUCCESS+alt re-opens the sheet. */
				oldDoc.alternativeAcknowledged = false;

				return oldDoc;
			});

			this.__isRunning.next(true);

			if (this.options.useTTL) {
				invariant(
					this.mode !== 'LBODTFLEX' || currentRoundIndex === 0,
					'[CapacityCheck] LBODTFLEX can only be resumed from roundIndex 0 to 1'
				);

				/**
				 * v2 PATCH returns the resource — no follow-up GET needed.
				 * We also extend TTL on resume so the BE keeps the reservation alive
				 * until the next polling PATCH lands.
				 */
				const capacityCheckStateResponse =
					await this.capacityCheckAPI.patchSearchV2(doc.capacityCheckId, {
						alternativesDirection,
						timeToLive: this.currentTTLSeconds,
						...(this.mode !== 'LBODTFLEX' && { roundIndex: nextRoundIndex }),
					});

				await this.applyServerState(generation, capacityCheckStateResponse);
			} else {
				await this.capacityCheckAPI.patchSearch(doc.capacityCheckId, {
					alternativesDirection,
					roundIndex: nextRoundIndex,
				});

				const capacityCheckStateResponse =
					await this.capacityCheckAPI.getSearch(doc.capacityCheckId);

				await this.applyServerState(generation, capacityCheckStateResponse);
			}

			this.startPolling();
		} catch (e: any) {
			this.logger.error('resume error', e);
			this.capacityCheckError$.next(e);
			this.destroy(true);
		}
	}

	private startPolling() {
		this.logger.info('startPolling');
		const doc = this.ensureCapacityCheckRxDoc;

		this.poller.start(doc.capacityCheckId$);
	}

	private get capacityCheckRxCollection() {
		return this.bl.db.collections['capacityCheck'] as RxCollection<
			ICapacityCheckDocType<ItineraryType>
		>;
	}

	public get $(): Observable<TCapacityCheckRxDocument<ItineraryType>> {
		return this.capacityCheckRxDoc$.pipe(
			filter((doc): doc is TCapacityCheckRxDocument<ItineraryType> => !!doc),
			switchMap((doc) => doc.$)
		);
	}

	public get state$(): Observable<
		DeepReadonlyObject<ICapacityCheckDocType<ItineraryType>>
	> {
		return this.$.pipe(map((doc) => doc.toJSON()));
	}

	/** Fires on SUCCESS with unacknowledged alternative. */
	public get alternativeVehicleTypeSuggested$(): Observable<true> {
		return this.state$.pipe(
			distinctUntilChanged((a, b) => a?.status === b?.status),
			filter(
				(state) =>
					state?.status === SearchStatusEnum.SUCCESS &&
					Boolean(state.result?.alternativeVehicleType) &&
					!state.alternativeAcknowledged
			),
			map(() => true)
		);
	}

	public get status$() {
		return this.capacityCheckRxDoc$.pipe(
			switchMap((doc) => (doc ? doc.status$ : NEVER))
		);
	}

	public get reservationCountdown$() {
		return this.state$.pipe(
			switchMap((capacityCheckState) =>
				capacityCheckState?.result?.reservedUntil
					? getCountdownTimer$(
							new Date(capacityCheckState.result.reservedUntil)
					  )
					: NEVER
			)
		);
	}

	public get expired$() {
		return this.capacityCheckRxDoc$.pipe(
			switchMap((doc) => (doc ? doc.status$ : NEVER)),
			first((status) => status === SearchStatusEnum.EXPIRED)
		);
	}

	public get cancelled$() {
		return this.capacityCheckRxDoc$.pipe(
			switchMap((doc) => (doc ? doc.status$ : NEVER)),
			first((status) => status === SearchStatusEnum.CANCELLED)
		);
	}

	/**
	 * Test-only introspection: is a poll armed right now? Not a prediction of the next
	 * cycle — assert continued polling behaviorally (count poll requests over time).
	 */
	public get unsafe_isPolling(): boolean {
		return this.poller.isPolling;
	}

	/** Pauses polling (e.g. while the app is backgrounded). TTL keep-alive covers the gap. */
	public pausePolling(): void {
		this.poller.pause();
	}

	public resumePolling(): void {
		this.poller.resume();
	}

	public get isDone() {
		const capacityCheckRxDoc = this.capacityCheckRxDoc;

		return Boolean(
			capacityCheckRxDoc?.status &&
				[SearchStatusEnum.SUCCESS, SearchStatusEnum.NOTHING_FOUND].includes(
					capacityCheckRxDoc.status
				)
		);
	}

	private initTeardownRequests() {
		/**
		 * Listens for teardown requests and runs teardown logic exclusively:
		 * 	- For each teardown request, waits for the first valid document (not deleted, has capacityCheckId).
		 * 	- Runs teardown via _teardownCapacityCheck, only once per request.
		 * 	- While teardown is running, all new requests are ignored (exhaustMap).
		 * 	- If an error occurs, it safely skips (catchError).
		 * */
		return this.__teardownRequest$
			.pipe(
				exhaustMap(({ silent }) =>
					defer(() =>
						this.$.pipe(
							withLatestFrom(this.__isRunning),
							filter(
								([doc, isRunning]) =>
									/** The possible cases are:
									 *  * teardown a running capacitycheck
									 *  * teardown a capacitycheck without the {capacityCheckId}, but running (waiting the POST to finish)
									 *  * teardown an old capacitycheck without capacityCheckId
									 *  */
									!isRunning || Boolean(doc?.capacityCheckId && !doc.deleted)
							),
							take(1),
							switchMap(([doc]) =>
								from(this._teardownCapacityCheck(doc, silent))
							),
							tap(() => this.__isRunning.next(false)),
							catchError(() => EMPTY)
						)
					)
				)
			)
			.subscribe();
	}

	private async _teardownCapacityCheck(
		doc: TCapacityCheckRxDocument<ItineraryType>,
		silent: boolean
	) {
		/**
		 * The search is dying — invalidate every response still in flight so a
		 * late poll/extendTTL cannot write into the doc being removed.
		 */
		this.writeGeneration++;

		const latestDoc = doc.getLatest();

		this.logger.info('teardown: DELETE', {
			capacityCheckId: latestDoc.capacityCheckId,
			silent,
		});

		if (latestDoc.capacityCheckId) {
			try {
				await this.capacityCheckAPI.deleteSearch(latestDoc.capacityCheckId);

				/**
				 * Optimistic update instead of refetching the data from the api.
				 * Skipped on `silent` teardown (internal error paths).
				 */
				if (!silent) {
					await latestDoc.incrementalModify((oldDoc) => {
						oldDoc.status = SearchStatusEnum.CANCELLED;
						return oldDoc;
					});
				}
			} catch (err) {
				this.logger.error('deleteSearch error', err);
			}
		}

		if (!latestDoc.deleted) {
			try {
				await latestDoc.incrementalRemove();
			} catch (err) {
				this.logger.error('incrementalRemove error', err);
			}
		}

		this.poller.dispose();
		this.__teardownSub.unsubscribe();
	}

	/**
	 * Extends the BE-side reservation TTL (MC-10032). No-op in legacy mode (`useTTL=false`).
	 */
	public async extendTTL(
		seconds: number = BANK_REDIRECT_TTL_SECONDS
	): Promise<void> {
		if (!this.options.useTTL) {
			this.logger.info(
				'[CapacityCheck] extendTTL called in legacy mode — no-op'
			);
			return;
		}

		const doc = this.capacityCheckRxDoc;

		if (!doc?.capacityCheckId) {
			this.logger.info(
				'[CapacityCheck] extendTTL skipped — no capacityCheckId yet'
			);
			return;
		}

		/**
		 * Update the shared TTL config BEFORE firing the PATCH. If a polling tick
		 * races in between our PATCH and the BE persisting it, the polling PATCH
		 * will carry the same extended value — so the BE (which takes the last
		 * received TTL) cannot shrink the reservation back to POLLING_TTL_SECONDS.
		 */
		this.currentTTLSeconds = seconds;

		const generation = this.writeGeneration;

		try {
			const res = await this.capacityCheckAPI.patchSearchV2(
				doc.capacityCheckId,
				{ timeToLive: seconds }
			);

			await this.applyServerState(generation, res);
		} catch (e: any) {
			this.logger.error('extendTTL error: ', e);
		}
	}

	/** `silent=true` skips the optimistic CANCELLED status update. */
	public destroy(silent = false) {
		this.logger.info('destroy', { silent });
		this.__teardownRequest$.next({ silent });
	}
}

export class LBODTFlexCapacityCheck extends CapacityCheck<'LBODTFLEX'> {
	protected override capacityCheckAPI: IGenericCapacityCheckAPI<'LBODTFLEX'>;

	constructor(bl: BusinessLayer, options?: TCapacityCheckOptions) {
		super(bl, options);

		this.mode = 'LBODTFLEX';

		this.capacityCheckAPI = new LBODTFLEXApi(this.bl.api, this.bl.db);
	}
}

export class PRODTCapacityCheck extends CapacityCheck<'PRODT'> {
	protected override capacityCheckAPI: IGenericCapacityCheckAPI<'PRODT'>;

	constructor(bl: BusinessLayer, options?: TCapacityCheckOptions) {
		super(bl, options);

		this.mode = 'PRODT';

		this.capacityCheckAPI = new PRODTApi(this.bl.api, this.bl.db);
	}

	override async start(payload: PrivateTripSearchRequestDto): Promise<void> {
		await this.ensureCapacityCheckRxDoc.incrementalPatch({
			vehicleType: payload.vehicleType,
		});

		return super.start(payload);
	}
}
